/**
 * Polaris Impression & Click Tracking Utilities
 *
 * Single source of truth for all impression and click tracking logic used by the
 * Homepage DynamicTabbedLayout (DTL) and its related files. Builds tracking strings
 * for Polaris HP DynamicTabbedLayout per the tracking format: position|description|linkType
 *
 * - position -> eVar164 (PAGE LOCATION)
 * - description -> eVar165 (LINK DESCRIPTION)
 *
 * Tab clicks:  HP-PolarisCluster|HP-Tab-Tab{N}-{TabName}|internal
 * Card clicks: HP-Tab-Tab{N}-{TabName}|HP-Card-Tab{N}-{TabName}-Card{M}-{Shape}-{Category}-{Desc}|internal
 *
 * For the DCA flow, its fallback and pure CF flow all use the same trackingProps builder.
 *
 * Retargeting slots use the CF card format with category "Retargeting" and the applied
 * use case as the trailing segment (Group | Property-RLM | Property-ADF) — see
 * buildRetargetingProps in utils/retargeting/retargetingRuntime.ts (WEB-207387).
 *
 */

import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
import type {
  DcaSlotTarget,
  SlotContent,
  SlotTrackingContext,
} from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

const CONTENT_TYPE_CATEGORY_MAP: Record<string, string> = {
  destinations: 'Destination',
  properties: 'Brand',
  rnb: 'RnB',
  offers: 'Offer',
};
const DEFAULT_TAB_CLICK_LOCATION = 'HP-PolarisCluster';

/** Strip the literal `-mbox` suffix so tracking uses the placement position, not the raw mbox name. */
function stripMboxSuffix(name: string): string {
  return name.replace(/-mbox$/i, '');
}

function capitalizeShape(size: string): string {
  if (!size) return '';
  return size.charAt(0).toUpperCase() + size.slice(1);
}

export function buildTabTrackingPrefix(tabIndex: number, tabName: string): string {
  return `HP-Tab-Tab${tabIndex}-${tabName}`;
}

export function resolveTabClickLocation(trackingContentPosition: string | undefined | null): string {
  return sanitizeTrackingValue(trackingContentPosition) || DEFAULT_TAB_CLICK_LOCATION;
}

export function mapContentTypeToCategory(contentType: string | undefined | null): string {
  if (!contentType) return '';
  return CONTENT_TYPE_CATEGORY_MAP[contentType.toLowerCase()] || contentType;
}

export function mapDcaTypeToCategory(metadata: Record<string, unknown> | undefined): string {
  if (!metadata) return 'Merch';
  const type = (metadata['type'] as string)?.toLowerCase();
  if (type === 'cobrand') return 'MerchCobrand';
  return 'MerchNonCobrand';
}

export function sanitizeTrackingValue(value: string | undefined | null): string {
  if (!value) return '';
  return value.replace(/[\s,|]+/g, '');
}

/**
 * Build the per-slotType marker inserted right after the card shape.
 *
 * - `geo-cf`  -> `Geotargeted`, optionally region-prefixed (e.g. `West-Geotargeted`).
 * - `retargeting` -> `Retargeted`.
 * - everything else (cf/dca/undefined) -> no marker (unchanged legacy string).
 *
 * The region key (e.g. "west") is author-configurable and comes through lower-cased,
 * so it is capitalized here to match the rest of the tracking segments.
 */
function buildSlotTypeMarker(slotType?: string, region?: string): string {
  switch (slotType) {
    case 'geo-cf': {
      const regionLabel = capitalizeShape(sanitizeTrackingValue(region));
      return regionLabel ? `${regionLabel}-Geotargeted` : 'Geotargeted';
    }
    case 'retargeting':
    default:
      return '';
  }
}

export function buildCardTrackingDescription(ctx: {
  tabIndex: number;
  tabName: string;
  cardNumber: number;
  shape: string;
  category: string;
  description: string;
  cta?: string;
  slotType?: string;
  region?: string;
}): string {
  const parts = [
    'HP-Card',
    `Tab${ctx.tabIndex}-${ctx.tabName}`,
    `Card${ctx.cardNumber}`,
    capitalizeShape(ctx.shape),
    buildSlotTypeMarker(ctx.slotType, ctx.region),
    ctx.category,
    ctx.description,
  ].filter(Boolean);
  if (ctx.cta) parts.push(ctx.cta);
  return parts.join('-');
}

export function resolveCardCategory(
  contentObj: Record<string, unknown>,
  slotType: 'cf' | 'dca' | string | undefined
): string {
  if (slotType === 'dca') {
    const metadata = (contentObj['metaData'] ?? contentObj['metadata']) as Record<string, unknown> | undefined;
    return mapDcaTypeToCategory(metadata);
  }
  return mapContentTypeToCategory(contentObj['contentType'] as string);
}

export function resolveCardDescription(
  contentObj: Record<string, unknown>,
  slotType: 'cf' | 'dca' | string | undefined
): string {
  if (slotType === 'dca') {
    const metadata = (contentObj['metaData'] ?? contentObj['metadata']) as Record<string, unknown> | undefined;
    return (
      sanitizeTrackingValue((metadata?.['evar48'] as string) || (contentObj['atCCeVar48'] as string)) ||
      sanitizeTrackingValue(contentObj['title'] as string) ||
      sanitizeTrackingValue(contentObj['headerLong'] as string) ||
      ''
    );
  }
  return (
    sanitizeTrackingValue(contentObj['trackingDescription'] as string) ||
    sanitizeTrackingValue(contentObj['title'] as string) ||
    sanitizeTrackingValue(contentObj['header'] as string) ||
    ''
  );
}

function resolveDcaMboxTrackingPosition(c: Record<string, unknown>): string {
  const mboxValue =
    typeof c['mboxName'] === 'string'
      ? c['mboxName']
      : typeof c['mboxParameter'] === 'string'
      ? c['mboxParameter']
      : '';
  return stripMboxSuffix(mboxValue.trim());
}

/**
 * Resolves the merchandising category from the content object.
 * @param contentObj - The content object to resolve the merchandising category from.
 * @param isDcaSlot - Whether the slot is a DCA slot.
 * @param metadata - The metadata object to resolve the merchandising category from. If the slot is a DCA slot, the metadata object is the metadata object of the DCA slot.
 * @param defaultValue - The default value to return if no merchandising category is found.
 * @returns The merchandising category.
 */
export function resolveMerchandisingCategory(
  contentObj: Record<string, unknown>,
  isDcaSlot: boolean,
  metadata?: Record<string, unknown>,
  defaultValue = 'nonCobrand'
): string {
  const authoredCategory =
    typeof contentObj['merchandisingCategory'] === 'string' ? contentObj['merchandisingCategory'].trim() : '';
  if (authoredCategory) return authoredCategory;

  const metadataType = isDcaSlot && typeof metadata?.['type'] === 'string' ? metadata['type'].trim() : '';
  if (metadataType) return metadataType;

  return defaultValue;
}

function resolveTrackingBoolean(value: unknown, fallbackValue: boolean): boolean {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return fallbackValue;
}

function resolveTrackingBooleans(
  contentObj: Record<string, unknown>,
  isDcaSlot: boolean
): Pick<TrackingPropsInterface, 'clickTrack' | 'impressionTrack' | 'impressionCount'> {
  return {
    clickTrack: resolveTrackingBoolean(contentObj['clickTrack'], true),
    impressionTrack: resolveTrackingBoolean(contentObj['impressionTrack'], isDcaSlot),
    impressionCount: resolveTrackingBoolean(contentObj['impressionCount'], isDcaSlot),
  };
}

// ─── DCA CF-fallback tracking ───────────────────────────────────────────────
function getDcaFallbackTrackingTarget(slot?: SlotContent): DcaSlotTarget | undefined {
  return slot?.type === 'cf' ? slot.dcaFallbackTarget : undefined;
}

function getDcaFallbackTrackingPosition(slot?: SlotContent): string {
  const target = getDcaFallbackTrackingTarget(slot);
  return typeof target?.mboxParameter === 'string' ? stripMboxSuffix(target.mboxParameter.trim()) : '';
}

/**
 * Fallback list3 entry (tab-switch batch): builds this slot's `position,msg=dca,type=,tag=undefined`
 * contribution when a DCA-reserved slot fell back to its evergreen CF (slot.type === 'cf').
 * Card-type agnostic. Returns null when there's no fallback position or authoring set
 * impressionTrack false (unauthored defaults to true, i.e. DCA semantics).
 */
export function buildDcaFallbackList3Entry(slot?: SlotContent): string | null {
  const position = getDcaFallbackTrackingPosition(slot);
  if (!position) return null;
  // Honor authored impressionTrack on the evergreen CF payload; default to true (DCA semantics).
  const contentObj = (slot?.payload && typeof slot.payload === 'object' ? slot.payload : {}) as Record<string, unknown>;
  if (!resolveTrackingBoolean(contentObj['impressionTrack'], true)) return null;
  return `${position},msg=dca,type=,tag=undefined`;
}

/**
 * Per-card tracking props for a DCA fallback (evergreen CF) slot, consumed by the card component
 * (e.g. LFC/PDC). Reuses the DCA position, honors authored clickTrack/impressionTrack/impressionCount
 * (unauthored → true), flags the card as DCA, and sets clickTrackValSeparator so the per-card Button
 * suppresses its own impression — the DTL batch is the single impression source.
 */
export function buildDcaFallbackTrackingProps(slot?: SlotContent): TrackingPropsInterface | null {
  const position = getDcaFallbackTrackingPosition(slot);
  if (!position) return null;

  // Honor authored clickTrack / impressionTrack / impressionCount from the evergreen CF payload.
  // A DCA-reserved slot whose Target call failed still renders its CF fallback and must fire the
  // merch impression/click calls, so unauthored flags default to DCA semantics (all true).
  const contentObj = (slot?.payload && typeof slot.payload === 'object' ? slot.payload : {}) as Record<string, unknown>;

  return {
    trackingContentPosition: position,
    trackingDescription: 'dca',
    trackingOfferType: '',
    trackingTag: 'undefined',
    atCCeVar48: '',
    ...resolveTrackingBooleans(contentObj, true),
    isDCAEnabled: true,
    // Match normal DCA: the separator keeps the per-card CTA Button from firing its own
    // event168|list3 impression (Button only fires it when impressionTrack && !clickTrackValSeparator),
    // while still writing the list3 CLICK value — so the DTL batch is the single impression source.
    clickTrackValSeparator: ';',
  } as TrackingPropsInterface;
}

// ─── Core tracking props builder ────────────────────────────────────────────
export function trackingProps(
  c: Record<string, unknown>,
  trackingCtx?: SlotTrackingContext,
  slotType?: 'cf' | 'dca' | string,
  slot?: SlotContent
): TrackingPropsInterface {
  const fallbackTrackingProps = buildDcaFallbackTrackingProps(slot);
  if (fallbackTrackingProps) return fallbackTrackingProps;
  const isDcaSlot = slotType === 'dca';

  const base = (trackingCtx?.trackingProperties ?? {}) as Record<string, unknown>;
  const metadata = isDcaSlot ? ((c['metaData'] ?? c['metadata']) as Record<string, unknown> | undefined) : undefined;
  const evar48 = ((metadata?.['evar48'] || c['atCCeVar48'] || c['eVar48'] || '') as string).trim();
  const tabTrackingContentPosition =
    typeof base['tabTrackingContentPosition'] === 'string' ? (base['tabTrackingContentPosition'] as string) : '';
  const category = resolveCardCategory(c, slotType);
  const description = resolveCardDescription(c, slotType);
  const cardTrackingDesc = buildCardTrackingDescription({
    tabIndex: trackingCtx?.tabIndex ?? 1,
    tabName: trackingCtx?.tabName ?? '',
    cardNumber: trackingCtx?.index ?? 1,
    shape: trackingCtx?.slotSize ?? 'square',
    category,
    description,
    slotType,
    region: trackingCtx?.region,
  });
  return {
    ...base,
    trackingContentPosition:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : resolveDcaMboxTrackingPosition(c) || (c['trackingContentPosition'] as string)
        : tabTrackingContentPosition || base['trackingContentPosition'] || (c['trackingContentPosition'] as string),
    trackingDescription:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingDescription'] as string) || (metadata?.['title'] as unknown as string)
        : cardTrackingDesc,
    trackingOfferType:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingOfferType'] as string) || (metadata?.['type'] as unknown as string)
        : base['trackingOfferType'],
    trackingTag:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingTag'] as string) ||
            (metadata?.['merTag'] as unknown as string) ||
            (metadata?.['offerCode'] as unknown as string)
        : base['trackingTag'],
    ...(slotType === 'dca' && evar48 && { atCCeVar48: evar48 }),
    ...resolveTrackingBooleans(c, isDcaSlot),
    // WEB-210419: for NON-cobrand DCA cards the per-card CTA Button must NOT fire its own
    // event168|list3 impression. The single source of the combined non-cobrand merch impression is
    // the Adobe Launch rule that reads window.atMArray (which each card populates via getClickTrackValue
    // when authored impressionTrack is true — separator-independent). The Button fires its OWN per-card
    // trackImpression only when `impressionTrack && !clickTrackValSeparator`, so setting the separator
    // suppresses that redundant per-card call (mirroring buildDcaFallbackTrackingProps for CF fallback).
    // Cobrand DCA keeps its own impression path (event168|event270|evar48), so it is excluded via `!evar48`.
    ...(slotType === 'dca' && !evar48 && { clickTrackValSeparator: ';' }),
    isDCAEnabled: slotType === 'dca' ? true : '',
    enableScrollingBehavior: c['enableScrollingBehavior'] ?? base['enableScrollingBehavior'],
  } as TrackingPropsInterface;
}

// ─── Non-cobrand DCA merchandising impression (event168|list3) ──────────────
function trackingValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function isDcaEnabled(value: unknown): boolean {
  return value === true || value === 'true';
}

/**
 * Live-DCA list3 entry (tab-switch batch): builds this NON-cobrand card's
 * `position,msg=description,type=offerType,tag=tag` contribution from its resolved tracking props.
 * Returns null when the card isn't DCA, has impressionTrack authored false, is excluded from the
 * non-cobrand batch (mango, cobrand, or carries an evar48 — those fire their own impression paths),
 * or is missing any required tracking field.
 */
export function buildNonCobrandDcaList3Entry(trackingProperties: Record<string, unknown> | undefined): string | null {
  if (!trackingProperties || !isDcaEnabled(trackingProperties['isDCAEnabled'])) return null;
  // Honor authored impressionTrack: skip the batch entry when authoring turned impressions off.
  if (trackingProperties['impressionTrack'] === false || trackingProperties['impressionTrack'] === 'false') return null;
  if (trackingProperties['isMango'] === true || trackingProperties['isMango'] === 'true') return null;
  if (trackingValue(trackingProperties['type']).toLowerCase() === 'cobrand') return null;
  if (trackingProperties['isCoBrand'] === true || trackingProperties['isCoBrand'] === 'true') return null;
  if (trackingValue(trackingProperties['atCCeVar48'])) return null;

  const position = trackingValue(trackingProperties['trackingContentPosition']);
  const description = trackingValue(trackingProperties['trackingDescription']);
  const offerType = trackingValue(trackingProperties['trackingOfferType']);
  const tag = trackingValue(trackingProperties['trackingTag']);
  if (!position || !description || !offerType || !tag) return null;

  return `${position},msg=${description},type=${offerType},tag=${tag}`;
}
